import { isDefined } from '@e-socna/utils';

/**
 * Resolver return type codes.
 * These values match the DocumentResolverReturnTypeEnum from both backend and frontend.
 */
export type ResolverReturnTypeCode =
  | 'Boolean'
  | 'Component'
  | 'CurrencyId'
  | 'Float'
  | 'Integer'
  | 'String'
  | 'UnitId'
  | 'VatId';

/**
 * Optional check functions for specific ID types.
 * Each function validates that the value is a valid ID of the given type.
 */
interface IdCheckFunctions {
  readonly checkCurrencyId: (value: unknown) => boolean;
  readonly checkUnitId: (value: unknown) => boolean;
  readonly checkVatId: (value: unknown) => boolean;
}

interface ValidationContext extends IdCheckFunctions {
  readonly resolverCode: string;
  readonly returnTypeCode: ResolverReturnTypeCode;
  readonly value: unknown;
}

interface ValidationResult {
  readonly isValid: boolean;
  readonly expectedType: string;
  readonly actualType: string;
}

const validateIdType = (
  value: unknown,
  actualType: string,
  returnTypeCode: 'CurrencyId' | 'UnitId' | 'VatId',
  checkSpecificId: (value: unknown) => boolean,
  // eslint-disable-next-line max-params
): ValidationResult => {
  if (!checkSpecificId(value)) {
    return {
      isValid: false,
      expectedType: `valid ${returnTypeCode}`,
      actualType: `unknown id "${value as string}"`,
    };
  }

  return {
    isValid: true,
    expectedType: `string (${returnTypeCode})`,
    actualType,
  };
};

/**
 * Validates that a resolved value matches the expected return type.
 * Used for runtime type checking after resolver execution.
 *
 * For ID types (CurrencyId, UnitId, VatId), you can optionally pass
 * allowedIds maps to also validate that the ID exists.
 */
export const validateResolverReturnType = ({
  returnTypeCode,
  value,
  checkCurrencyId,
  checkUnitId,
  checkVatId,
}: Omit<ValidationContext, 'resolverCode'>): ValidationResult => {
  const actualType = typeof value;

  switch (returnTypeCode) {
    case 'Boolean':
      return {
        isValid: actualType === 'boolean',
        expectedType: 'boolean',
        actualType,
      };

    case 'Component':
      return {
        isValid: isDefined(value) && actualType === 'object',
        expectedType: 'object (component)',
        actualType: value === null ? 'null' : actualType,
      };

    case 'CurrencyId':
      return validateIdType(value, actualType, returnTypeCode, checkCurrencyId);

    case 'UnitId':
      return validateIdType(value, actualType, returnTypeCode, checkUnitId);

    case 'VatId':
      return validateIdType(value, actualType, returnTypeCode, checkVatId);

    case 'Float':
      return {
        isValid: actualType === 'number' && !Number.isNaN(value),
        expectedType: 'number (float)',
        actualType: Number.isNaN(value) ? 'NaN' : actualType,
      };

    case 'Integer':
      return {
        isValid:
          actualType === 'number' &&
          !Number.isNaN(value) &&
          Number.isInteger(value),
        expectedType: 'number (integer)',
        actualType: Number.isNaN(value)
          ? 'NaN'
          : Number.isInteger(value)
            ? actualType
            : 'float',
      };

    case 'String':
      return {
        isValid: actualType === 'string',
        expectedType: 'string',
        actualType,
      };

    default: {
      return {
        isValid: false,
        expectedType: 'unknown return type code',
        actualType,
      };
    }
  }
};

/**
 * Validates a resolved value and throws if invalid.
 * Useful for backend validation where we want to fail fast.
 */
export const ensureValidResolverReturnType = (
  context: ValidationContext,
): void => {
  const result = validateResolverReturnType({
    returnTypeCode: context.returnTypeCode,
    value: context.value,
    checkCurrencyId: context.checkCurrencyId,
    checkUnitId: context.checkUnitId,
    checkVatId: context.checkVatId,
  });

  if (!result.isValid) {
    throw new Error(
      `Resolver "${context.resolverCode}" returned invalid type. ` +
        `Expected ${result.expectedType}, got ${result.actualType}.`,
    );
  }
};
